/* eslint-disable global-require */
import {
  useContext,
  useEffect,
  useState,
  createContext,
  ReactNode,
} from 'react';

import type { ERegionCode } from '@sureapp/platform-sdk';
import { mergeConfig } from '@sureapp/wl-toggle-common/dist/services/mergeConfig';

import type { TConfig, TConfigParams, TTenant } from '@@/auto/interfaces';
import { useFeatureFlags } from '@@/common/services/useFeatureFlags';

import { getComputed } from './tenant';
import { useRegion } from './useRegion';

type TConfigContextValue = {
  config: TConfig;
  regionCode?: ERegionCode;
  tenant: {
    is: ReturnType<typeof getComputed>;
    name: TTenant;
  };
};

// Display name : data-tenant value
const tenant = {
  Toyota: 'toyota',
  Toggle: 'toggle',
  'Credit Karma': 'credit-karma',
  Carvana: 'carvana',
} as const;

const getContextValue = (config: TConfig, regionCode?: ERegionCode) => ({
  config,
  regionCode,
  tenant: {
    is: getComputed(config),
    name: tenant[config?.tenantDisplayName ?? 'Toggle'],
  },
});

const ConfigContext = createContext<TConfigContextValue>(
  // Since wlConfigs can import components, and components often use useConfig,
  // it's possible for a circular dependency to occur. When this happens,
  // "import wlConfig from '@@/contants/wlConfig';" actually sets the value of
  // wlConfig to undefined, causing issues downstream where components try
  // to read values off of the config.
  //
  // To work around this, we dynamically import the configs on render and avoid
  // rendering anything further in the React tree until the configs have loaded.
  // So even though we pass null here as an initial value for the context,
  // it's not possible for any component using useConfig() to ever receive null
  // as the context value.

  // @ts-expect-error
  null
);

export function useConfig() {
  return useContext(ConfigContext);
}

type TOpts = {
  overrideConfig?: DeepPartial<TConfig>;
  regionCode?: ERegionCode;
} & TConfigParams;

export function loadConfigs({
  overrideConfig,
  regionCode,
  isFeatureEnabled,
}: TOpts) {
  const {
    getRegionConfigByCode,
  } = require('@@/auto/services/generateRegionConfig');
  const { default: defaultConfig } = require('@@/auto/constants/wlConfig');

  return overrideConfig
    ? mergeConfig(defaultConfig, overrideConfig || {})
    : getRegionConfigByCode(regionCode, { isFeatureEnabled });
}

function useConfigValue(opts: TOpts) {
  const [mergedConfig, setMergedConfig] = useState<TConfig | null>(null);

  useEffect(function onMount() {
    setMergedConfig(loadConfigs(opts));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return mergedConfig;
}

export function ConfigProvider({
  children,
  overrideConfig,
  useGetConfigValue = useConfigValue,
}: {
  overrideConfig?: DeepPartial<TConfig>;
  children: ReactNode;
  useGetConfigValue?: typeof useConfigValue;
}) {
  const { isFeatureEnabled } = useFeatureFlags();
  const { regionCode } = useRegion();
  const mergedConfig = useGetConfigValue({
    overrideConfig,
    regionCode,
    isFeatureEnabled,
  });

  if (!mergedConfig) {
    return null;
  }

  return (
    <ConfigContext.Provider value={getContextValue(mergedConfig, regionCode)}>
      {children}
    </ConfigContext.Provider>
  );
}
